import { useMemo } from "react";
import { useTaxonomyContext } from "../../../../contexts/Taxonomy/TaxonomyContext";
import { useWorkspaceStore } from "../../../../store/workspace/WorkspaceStore";
import { Market } from "../../../../types/workspace";
import useMatchProductsById from "../../View/MarketLandscape/hooks/useMatchProductsById";
import { FilterLabel } from "../types";
import { getEmployeeAndRevenueRangeLabel, getLabelText } from "../utils";
import { useGeographyData } from "./useGeographyData";
import { useIndustryData } from "./useIndustryData";

export const useGetFilterLabels = () => {
  const selectedMarket = useWorkspaceStore((state) => state.selectedMarket);

  const configuration = useWorkspaceStore(
    (state) => state.draft?.configuration
  );

  const includedProductIds = useMemo(
    () =>
      (selectedMarket === Market.SOM && configuration?.som.productIds) || [],
    [configuration, selectedMarket]
  );
  const excludedProductIds = useMemo(
    () =>
      (selectedMarket === Market.SOM &&
        configuration?.som.excludedProductIds) ||
      [],
    [configuration, selectedMarket]
  );

  const {
    matchedProductsById: includedProducts,
    loading: isIncludedProductsLoading,
  } = useMatchProductsById(includedProductIds);

  const {
    matchedProductsById: excludedProducts,
    loading: isExcludedProductsLoading,
  } = useMatchProductsById(excludedProductIds);

  const {
    industryData,
    labels: industryLabels,
    isLoading: isIndustryLoading,
  } = useIndustryData(
    ((selectedMarket === Market.SAM || selectedMarket === Market.SOM) &&
      configuration?.sam.industry) ||
      undefined
  );

  const {
    geographyData,
    labels: geoLabels,
    isLoading: isGeographyLoading,
  } = useGeographyData(
    ((selectedMarket === Market.SAM || selectedMarket === Market.SOM) &&
      configuration?.sam.geography) || {
      regionsIds: [],
      countryIds: [],
      stateIds: [],
      excludedCountryIds: [],
    }
  );

  const { spendCategories } = useTaxonomyContext();
  const spendCategoryIds = useMemo(
    () => configuration?.tam.spendCategoryIds || [],
    [configuration?.tam.spendCategoryIds]
  );
  const spendCategoryWeightsData = useMemo(
    () => configuration?.tam.spendCategoryWeights || [],
    [configuration?.tam.spendCategoryWeights]
  );

  const spendCategoryWeights = useMemo(() => {
    const data: { [id: string]: number } = {};

    if (!spendCategoryWeightsData) {
      return data;
    }

    spendCategoryWeightsData
      .filter((spendCategoryWeight) =>
        spendCategoryIds?.includes(spendCategoryWeight.id)
      )
      .map((item) => {
        data[item.id] = item.weight;
      });
    return data;
  }, [spendCategoryWeightsData, spendCategoryIds]);

  const isLoading = useMemo(
    () =>
      isIndustryLoading ||
      isGeographyLoading ||
      isIncludedProductsLoading ||
      isExcludedProductsLoading,
    [
      isIndustryLoading,
      isGeographyLoading,
      isIncludedProductsLoading,
      isExcludedProductsLoading,
    ]
  );

  // Combine all labels
  const labels = useMemo<FilterLabel[]>(() => {
    const allLabels: FilterLabel[] = [];

    if (spendCategoryIds?.length) {
      if (spendCategoryIds.length > 1) {
        allLabels.push({
          text: `IT Spend: ${spendCategoryIds.length} selected`,
          type: "spend",
        });
      } else {
        const [singleId] = spendCategoryIds;
        const spendCategory = spendCategories.find(({ id }) => id === singleId);
        //check if spendCategory is undefined
        if (!spendCategory) {
          allLabels.push({
            text: "IT Spend: unknown",
            type: "spend",
          });
        } else {
          let label = `IT Spend: `;
          label += spendCategory.name;

          allLabels.push({
            text: label,
            type: "spend",
          });
        }
      }
    }

    const rangeLabels =
      selectedMarket === Market.SOM
        ? getEmployeeAndRevenueRangeLabel(configuration?.som)
        : [];

    const productLabelText = getLabelText(
      "Products",
      includedProducts.length,
      excludedProducts.length
    );

    const productLabels: FilterLabel[] =
      selectedMarket === Market.SOM
        ? productLabelText
          ? [
              {
                text: productLabelText,
                type: "products",
              },
            ]
          : []
        : [];

    return [
      ...allLabels,
      ...(((selectedMarket === Market.SAM || selectedMarket === Market.SOM) &&
        industryLabels) ||
        []),
      ...(((selectedMarket === Market.SAM || selectedMarket === Market.SOM) &&
        geoLabels) ||
        []),
      ...rangeLabels,
      ...productLabels,
    ];
  }, [
    spendCategoryIds,
    spendCategories,
    industryLabels,
    geoLabels,
    excludedProducts,
    includedProducts,
    configuration,
    selectedMarket,
  ]);

  return {
    labels,
    productData: {
      includedProducts,
      excludedProducts,
      employeeRange:
        (selectedMarket === Market.SOM && configuration?.som.employeeRange) ||
        undefined,
      revenueRange:
        (selectedMarket === Market.SOM && configuration?.som.revenueRange) ||
        undefined,
    },
    spendCategoryData: spendCategories.filter((t: { id: string }) =>
      spendCategoryIds?.includes(t.id)
    ),
    spendCategoryWeights,
    industryData,
    geographyData,
    isLoading,
  };
};
